import { RadioGroup, RadioGroupItem, cn } from '@parrot-software/design-system';
import type { ReactNode } from 'react';

export type RadioCardOption = {
  value: string;
  label: string;
  description?: string;
  icon?: ReactNode;
};

type RadioCardGridProps = {
  columns?: number;
  onChange: (value: string) => void;
  options: RadioCardOption[];
  value: string;
};

const GRID_COLS: Record<number, string> = {
  2: 'grid-cols-1 lg:grid-cols-2',
  3: 'grid-cols-1 lg:grid-cols-3',
};

/**
 * Grid of selectable radio cards with icon and label.
 * Reusable across wizard steps that use the radio card pattern.
 *
 * @example
 * <RadioCardGrid
 *   options={[{ value: 'yes', label: 'Sí', icon: <CheckIcon /> }]}
 *   onChange={(val) => setValue(val)}
 *   value={selectedValue}
 * />
 */
export const RadioCardGrid = ({ columns = 2, onChange, options, value }: RadioCardGridProps) => {
  const gridClass = GRID_COLS[columns] ?? 'grid-cols-2';

  return (
    <RadioGroup className={cn('grid gap-4', gridClass)} onValueChange={onChange} value={value}>
      {options.map((option) => {
        const isSelected = value === option.value;
        return (
          <label
            className={cn(
              'flex cursor-pointer flex-col gap-2 rounded-xl border p-5 shadow-xs transition-colors',
              isSelected ? 'border-2 border-primary' : 'border-border hover:border-gray-300',
            )}
            key={option.value}
          >
            <RadioGroupItem className="sr-only" id={option.value} value={option.value} />
            {option.icon && <span className="text-foreground">{option.icon}</span>}
            <div className="flex flex-col gap-0.5">
              <span className="text-base font-medium leading-6 text-foreground">{option.label}</span>
              {option.description && (
                <span className="text-sm font-normal leading-5 text-muted-foreground">{option.description}</span>
              )}
            </div>
          </label>
        );
      })}
    </RadioGroup>
  );
};
